// Lens research data for FoodLens AI
// Each lens includes research-backed information, health concerns, and scientific references

export const lensesData = {
  'artificial_colors': {
    id: 'artificial_colors',
    name: 'Artificial Colors',
    tagline: 'Linked to hyperactivity in children',
    tier: 'free',
    icon: '🎨',
    color: '#FF6B6B',
    description: 'Synthetic food dyes derived from petroleum are added to thousands of processed foods to make them more visually appealing. These dyes have been linked to behavioral issues in children and are banned or require warning labels in many countries outside the US.',
    keyFacts: [
      'The Southampton study found artificial colors increased hyperactivity in children',
      'Several dyes are banned in European countries but allowed in the US',
      'Red 3 is banned in cosmetics due to cancer concerns but still allowed in food',
      'Over 15 million pounds of food dyes are used annually in the US'
    ],
    ingredients: ['Red 40', 'Yellow 5', 'Yellow 6', 'Blue 1', 'Blue 2', 'Red 3', 'Green 3'],
    references: [
      {
        title: 'Food additives and hyperactive behaviour in 3-year-old and 8/9-year-old children in the community: a randomised, double-blinded, placebo-controlled trial',
        authors: 'McCann D, Barrett A, Cooper A, et al.',
        publication: 'The Lancet',
        year: 2007,
        doi: '10.1016/S0140-6736(07)61306-3',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17825405/'
      },
      {
        title: 'Artificial food colors and attention-deficit/hyperactivity symptoms: conclusions to dye for',
        authors: 'Stevens LJ, Kuczek T, Burgess JR, et al.',
        publication: 'Neurotherapeutics',
        year: 2012,
        doi: '10.1007/s13311-012-0133-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22864801/'
      },
      {
        title: 'Toxicology and carcinogenesis studies of FD & C Red No. 40',
        authors: 'National Toxicology Program',
        publication: 'NTP Technical Report',
        year: 1993,
        doi: null,
        url: 'https://ntp.niehs.nih.gov/publications/reports/tr/400s/tr441'
      }
    ]
  },

  'seed_oils': {
    id: 'seed_oils',
    name: 'Seed Oils',
    tagline: 'High in inflammatory omega-6 fatty acids',
    tier: 'free',
    icon: '🌻',
    color: '#FFA500',
    description: 'Industrial seed oils like canola, soybean, and sunflower oil are extracted using high heat and chemical solvents. They are high in omega-6 fatty acids which, when consumed in excess relative to omega-3s, may promote inflammation throughout the body.',
    keyFacts: [
      'The omega-6 to omega-3 ratio in Western diets has increased from 1:1 to over 20:1',
      'Seed oils are unstable and can oxidize when heated, creating harmful compounds',
      'These oils were virtually non-existent in human diets before the 1900s',
      'Soybean oil alone accounts for over 7% of calories in the American diet'
    ],
    ingredients: ['Canola Oil', 'Soybean Oil', 'Sunflower Oil', 'Safflower Oil', 'Corn Oil', 'Cottonseed Oil', 'Grapeseed Oil'],
    references: [
      {
        title: 'Soybean Oil Is More Obesogenic and Diabetogenic than Coconut Oil and Fructose in Mouse',
        authors: 'Deol P, Evans JR, Dhahbi J, et al.',
        publication: 'PLOS ONE',
        year: 2015,
        doi: '10.1371/journal.pone.0132672',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26200659/'
      },
      {
        title: 'Health Implications of High Dietary Omega-6 Polyunsaturated Fatty Acids',
        authors: 'Patterson E, Wall R, Fitzgerald GF, et al.',
        publication: 'Journal of Nutrition and Metabolism',
        year: 2012,
        doi: '10.1155/2012/539426',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22570770/'
      },
      {
        title: 'The importance of the ratio of omega-6/omega-3 essential fatty acids',
        authors: 'Simopoulos AP',
        publication: 'Biomedicine & Pharmacotherapy',
        year: 2002,
        doi: '10.1016/s0753-3322(02)00253-6',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12442909/'
      }
    ]
  },

  'nuts': {
    id: 'nuts',
    name: 'Tree Nuts & Peanuts',
    tagline: 'Major allergens affecting millions',
    tier: 'free',
    icon: '🥜',
    color: '#8B4513',
    description: 'Tree nuts and peanuts are among the most common food allergens, affecting approximately 1-2% of the population. Allergic reactions can range from mild symptoms to life-threatening anaphylaxis, making ingredient awareness critical for affected individuals.',
    keyFacts: [
      'Peanut allergies have more than tripled in children since 1997',
      'Tree nut and peanut allergies are among the leading causes of food-induced anaphylaxis',
      'Cross-contamination during manufacturing is a significant concern',
      'Many people with tree nut allergies can safely eat some nuts but not others'
    ],
    ingredients: ['Peanuts', 'Almonds', 'Cashews', 'Walnuts', 'Pecans', 'Pistachios', 'Macadamia Nuts', 'Brazil Nuts', 'Hazelnuts'],
    references: [
      {
        title: 'The prevalence, severity, and distribution of childhood food allergy in the United States',
        authors: 'Gupta RS, Springston EE, Warrier MR, et al.',
        publication: 'Pediatrics',
        year: 2011,
        doi: '10.1542/peds.2011-0204',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21690110/'
      },
      {
        title: 'Peanut allergy: an overview',
        authors: 'Al-Ahmed N, Alsowaidi S, Vadas P',
        publication: 'Canadian Medical Association Journal',
        year: 2008,
        doi: '10.1503/cmaj.070611',
        url: 'https://pubmed.ncbi.nlm.nih.gov/18519897/'
      }
    ]
  },

  'dairy': {
    id: 'dairy',
    name: 'Dairy',
    tagline: 'Common allergen and digestive irritant',
    tier: 'free',
    icon: '🥛',
    color: '#87CEEB',
    description: 'Dairy products contain lactose and milk proteins that many people cannot properly digest. Lactose intolerance affects an estimated 68% of the world\'s population, while milk protein allergies are among the most common food allergies in children.',
    keyFacts: [
      'Approximately 68% of the global population has reduced ability to digest lactose',
      'Lactose intolerance rates vary significantly by ethnicity (up to 90% in some Asian populations)',
      'Milk allergy and lactose intolerance are different conditions with distinct symptoms',
      'Casein and whey are the two main proteins in milk that can trigger allergic reactions'
    ],
    ingredients: ['Milk', 'Cheese', 'Butter', 'Cream', 'Whey', 'Casein', 'Lactose', 'Yogurt'],
    references: [
      {
        title: 'Lactose intolerance in adults: biological mechanism and dietary management',
        authors: 'Misselwitz B, Butter M, Verbeke K, Fox MR',
        publication: 'Nutrients',
        year: 2019,
        doi: '10.3390/nu11051082',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31142329/'
      },
      {
        title: 'Cow\'s milk protein allergy in children: a practical guide',
        authors: 'Lifschitz C, Szajewska H',
        publication: 'Italian Journal of Pediatrics',
        year: 2015,
        doi: '10.1186/s13052-015-0119-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25712216/'
      }
    ]
  },

  'low_sugar': {
    id: 'low_sugar',
    name: 'Hidden Sugars',
    tagline: 'Disguised under 60+ different names',
    tier: 'free',
    icon: '🔍',
    color: '#FFD700',
    description: 'Sugar appears on ingredient labels under more than 60 different names, making it difficult for consumers to identify. From high fructose corn syrup to maltodextrin, these hidden sugars contribute to obesity, diabetes, and metabolic syndrome.',
    keyFacts: [
      'The average American consumes about 77 grams of added sugar per day (3x the recommended amount)',
      'Sugar has over 60 different names used on food labels',
      'High fructose corn syrup is metabolized differently than glucose and may promote fat storage',
      'Fruit juice concentrates are often used as "natural" sweeteners but have similar effects to sugar'
    ],
    ingredients: ['High Fructose Corn Syrup', 'Maltodextrin', 'Dextrose', 'Sucrose', 'Fruit Juice Concentrate', 'Cane Sugar', 'Corn Syrup', 'Agave'],
    references: [
      {
        title: 'Consuming fructose-sweetened, not glucose-sweetened, beverages increases visceral adiposity and lipids',
        authors: 'Stanhope KL, Schwarz JM, Keim NL, et al.',
        publication: 'Journal of Clinical Investigation',
        year: 2009,
        doi: '10.1172/JCI37385',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19381015/'
      },
      {
        title: 'Added Sugars and Cardiovascular Disease Risk in Children',
        authors: 'Vos MB, Kaar JL, Welsh JA, et al.',
        publication: 'Circulation',
        year: 2017,
        doi: '10.1161/CIR.0000000000000439',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27550974/'
      },
      {
        title: 'Guideline: Sugars intake for adults and children',
        authors: 'World Health Organization',
        publication: 'WHO Guidelines',
        year: 2015,
        doi: null,
        url: 'https://www.who.int/publications/i/item/9789241549028'
      }
    ]
  },

  'artificial_sweeteners': {
    id: 'artificial_sweeteners',
    name: 'Artificial Sweeteners',
    tagline: 'May disrupt gut microbiome and metabolism',
    tier: 'premium',
    icon: '🧪',
    color: '#9370DB',
    description: 'Non-nutritive sweeteners like aspartame, sucralose, and saccharin provide sweetness without calories, but research suggests they may alter gut bacteria, affect glucose metabolism, and paradoxically contribute to weight gain through various mechanisms.',
    keyFacts: [
      'Artificial sweeteners can alter gut microbiome composition within days',
      'Some studies show they may actually increase appetite and sugar cravings',
      'Aspartame contains phenylalanine, which people with PKU must avoid',
      'Recent research linked erythritol to increased cardiovascular risk'
    ],
    ingredients: ['Aspartame', 'Sucralose', 'Saccharin', 'Acesulfame-K', 'Stevia', 'Erythritol', 'Xylitol', 'Sorbitol'],
    references: [
      {
        title: 'Artificial sweeteners induce glucose intolerance by altering the gut microbiota',
        authors: 'Suez J, Korem T, Zeevi D, et al.',
        publication: 'Nature',
        year: 2014,
        doi: '10.1038/nature13793',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25231862/'
      },
      {
        title: 'The artificial sweetener erythritol and cardiovascular event risk',
        authors: 'Witkowski M, Nemet I, Alamri H, et al.',
        publication: 'Nature Medicine',
        year: 2023,
        doi: '10.1038/s41591-023-02223-9',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36848836/'
      },
      {
        title: 'Sucralose Affects Glycemic and Hormonal Responses to an Oral Glucose Load',
        authors: 'Pepino MY, Tiemann CD, Patterson BW, et al.',
        publication: 'Diabetes Care',
        year: 2013,
        doi: '10.2337/dc12-2221',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23633524/'
      }
    ]
  },

  'gums_thickeners': {
    id: 'gums_thickeners',
    name: 'Gums & Thickeners',
    tagline: 'May cause digestive issues in sensitive individuals',
    tier: 'free',
    icon: '🧈',
    color: '#A0522D',
    description: 'Gums, thickeners, and emulsifiers are added to processed foods to improve texture and shelf stability. While generally recognized as safe, some of these additives—particularly carrageenan—have been linked to gut inflammation and digestive issues.',
    keyFacts: [
      'Carrageenan has been shown to cause inflammation in laboratory studies',
      'These additives are found in many "healthy" foods like almond milk and yogurt',
      'Some emulsifiers may damage the intestinal mucus layer',
      'People with IBS or digestive issues may be more sensitive to these ingredients'
    ],
    ingredients: ['Carrageenan', 'Xanthan Gum', 'Guar Gum', 'Locust Bean Gum', 'Cellulose Gum', 'Polysorbate 80', 'Lecithin'],
    references: [
      {
        title: 'Review of harmful gastrointestinal effects of carrageenan in animal experiments',
        authors: 'Tobacman JK',
        publication: 'Environmental Health Perspectives',
        year: 2001,
        doi: '10.1289/ehp.01109983',
        url: 'https://pubmed.ncbi.nlm.nih.gov/11673690/'
      },
      {
        title: 'Dietary emulsifiers impact the mouse gut microbiota promoting colitis and metabolic syndrome',
        authors: 'Chassaing B, Koren O, Goodrich JK, et al.',
        publication: 'Nature',
        year: 2015,
        doi: '10.1038/nature14232',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25731162/'
      },
      {
        title: 'Carrageenan and Ulcerative Colitis: A Review',
        authors: 'Martino JV, Van Limbergen J, Cahill LE',
        publication: 'Frontiers in Pediatrics',
        year: 2017,
        doi: '10.3389/fped.2017.00096',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28503545/'
      }
    ]
  },

  'preservatives': {
    id: 'preservatives',
    name: 'Preservatives',
    tagline: 'Some classified as possible carcinogens',
    tier: 'free',
    icon: '🧴',
    color: '#DC143C',
    description: 'Chemical preservatives extend shelf life but some have been linked to serious health concerns. BHA is classified as "reasonably anticipated to be a human carcinogen" by the National Toxicology Program, while sodium benzoate can form benzene when combined with vitamin C.',
    keyFacts: [
      'BHA is classified as a possible human carcinogen by the IARC',
      'Sodium benzoate + vitamin C can form benzene, a known carcinogen',
      'BHA and BHT are banned in several countries but allowed in the US',
      'These preservatives can accumulate in body fat over time'
    ],
    ingredients: ['BHA', 'BHT', 'TBHQ', 'Sodium Benzoate', 'Potassium Sorbate', 'Sulfites', 'Propyl Gallate'],
    references: [
      {
        title: 'Butylated hydroxyanisole (BHA)',
        authors: 'National Toxicology Program',
        publication: 'Report on Carcinogens, 14th Edition',
        year: 2016,
        doi: null,
        url: 'https://ntp.niehs.nih.gov/ntp/roc/content/profiles/butylatedhydroxyanisole.pdf'
      },
      {
        title: 'Carcinogenicity of butylated hydroxyanisole in F344 rats',
        authors: 'Ito N, Fukushima S, Hagiwara A, et al.',
        publication: 'Journal of the National Cancer Institute',
        year: 1983,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/6571944/'
      },
      {
        title: 'Formation of benzene from benzoic acid in the presence of ascorbic acid',
        authors: 'Gardner LK, Lawrence GD',
        publication: 'Journal of Agricultural and Food Chemistry',
        year: 1993,
        doi: '10.1021/jf00032a032',
        url: 'https://pubmed.ncbi.nlm.nih.gov/10554218/'
      }
    ]
  },

  'nitrates': {
    id: 'nitrates',
    name: 'Nitrates & Nitrites',
    tagline: 'WHO Group 1 carcinogen in processed meats',
    tier: 'free',
    icon: '🥓',
    color: '#B22222',
    description: 'Nitrates and nitrites are used to cure processed meats, giving them their characteristic pink color and preventing bacterial growth. When heated or digested, they can form nitrosamines—potent carcinogens. The WHO has classified processed meats containing these compounds as Group 1 carcinogens.',
    keyFacts: [
      'WHO classifies processed meats with nitrites as Group 1 carcinogens (same as tobacco)',
      'Nitrosamines form when nitrites are heated, especially during frying',
      '"Natural" alternatives like celery powder contain the same compounds',
      'Eating 50g of processed meat daily increases colorectal cancer risk by 18%'
    ],
    ingredients: ['Sodium Nitrite', 'Sodium Nitrate', 'Potassium Nitrite', 'Potassium Nitrate', 'Celery Powder', 'Celery Juice', 'Curing Salt'],
    references: [
      {
        title: 'Carcinogenicity of consumption of red and processed meat',
        authors: 'Bouvard V, Loomis D, Guyton KZ, et al. (IARC Working Group)',
        publication: 'The Lancet Oncology',
        year: 2015,
        doi: '10.1016/S1470-2045(15)00444-1',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26514947/'
      },
      {
        title: 'IARC Monographs evaluate consumption of red meat and processed meat',
        authors: 'International Agency for Research on Cancer',
        publication: 'IARC Press Release',
        year: 2015,
        doi: null,
        url: 'https://www.iarc.who.int/wp-content/uploads/2018/07/pr240_E.pdf'
      },
      {
        title: 'Processed meat consumption and stomach cancer risk: a meta-analysis',
        authors: 'Zhu HC, Yang X, Xu LP, et al.',
        publication: 'PLOS ONE',
        year: 2013,
        doi: '10.1371/journal.pone.0081175',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24312534/'
      }
    ]
  },

  'ultra_processed': {
    id: 'ultra_processed',
    name: 'Ultra-Processed Indicators',
    tagline: 'NOVA Group 4 - linked to mortality and disease',
    tier: 'free',
    icon: '🏭',
    color: '#4B0082',
    description: 'Ultra-processed foods (NOVA Group 4) contain industrial ingredients rarely used in home cooking—hydrolyzed proteins, modified starches, and flavor enhancers. Studies consistently link high consumption to obesity, heart disease, cancer, and early death.',
    keyFacts: [
      'Ultra-processed foods now make up over 60% of calories in the American diet',
      'Each 10% increase in ultra-processed food consumption is linked to 14% higher mortality',
      'These foods are engineered to be hyper-palatable and easy to overconsume',
      'The NOVA classification system identifies 4 levels of food processing'
    ],
    ingredients: ['Hydrolyzed Protein', 'Modified Starch', 'MSG', 'Maltodextrin', 'Protein Isolate', 'Natural Flavors', 'Artificial Flavors'],
    references: [
      {
        title: 'Ultra-processed food consumption and risk of type 2 diabetes',
        authors: 'Srour B, Fezeu LK, Kesse-Guyot E, et al.',
        publication: 'JAMA Internal Medicine',
        year: 2020,
        doi: '10.1001/jamainternmed.2019.5942',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31841598/'
      },
      {
        title: 'Association Between Ultraprocessed Food Consumption and Risk of Mortality',
        authors: 'Rico-Campà A, Martínez-González MA, Alvarez-Alvarez I, et al.',
        publication: 'JAMA Internal Medicine',
        year: 2019,
        doi: '10.1001/jamainternmed.2019.0618',
        url: 'https://pubmed.ncbi.nlm.nih.gov/31121600/'
      },
      {
        title: 'Ultra-Processed Foods and Health Outcomes: A Narrative Review',
        authors: 'Elizabeth L, Machado P, Zinöcker M, et al.',
        publication: 'Nutrients',
        year: 2020,
        doi: '10.3390/nu12071955',
        url: 'https://pubmed.ncbi.nlm.nih.gov/32630022/'
      },
      {
        title: 'The UN Decade of Nutrition, the NOVA food classification and the trouble with ultra-processing',
        authors: 'Monteiro CA, Cannon G, Moubarac JC, et al.',
        publication: 'Public Health Nutrition',
        year: 2018,
        doi: '10.1017/S1368980017000234',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28322183/'
      }
    ]
  },

  'celiac': {
    id: 'celiac',
    name: 'Celiac Disease (Gluten)',
    tagline: 'Triggers autoimmune response in celiac patients',
    tier: 'free',
    icon: '🌾',
    color: '#D4A574',
    description: 'Celiac disease is an autoimmune disorder where ingesting gluten — a protein found in wheat, barley, and rye — causes damage to the small intestine. Affecting roughly 1 in 100 people worldwide, even trace amounts of gluten can trigger symptoms and intestinal damage in those with celiac disease.',
    keyFacts: [
      'Celiac disease affects approximately 1% of the global population, though most cases remain undiagnosed',
      'Even 10-50 milligrams of gluten (a fraction of a bread crumb) can cause intestinal damage',
      'Gluten hides in unexpected places: soy sauce, malt vinegar, many seasonings, and medications',
      'Oats are naturally gluten-free but frequently contaminated during processing — look for certified gluten-free oats'
    ],
    ingredients: ['Wheat', 'Barley', 'Rye', 'Triticale', 'Semolina', 'Spelt', 'Farro', 'Malt', 'Seitan', 'Couscous', 'Bulgur'],
    references: [
      {
        title: 'Celiac disease',
        authors: 'Lebwohl B, Sanders DS, Green PHR',
        publication: 'The Lancet',
        year: 2018,
        doi: '10.1016/S0140-6736(17)31796-8',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28760445/'
      },
      {
        title: 'ACG Clinical Guidelines: Diagnosis and Management of Celiac Disease',
        authors: 'Rubio-Tapia A, Hill ID, Kelly CP, et al.',
        publication: 'American Journal of Gastroenterology',
        year: 2013,
        doi: '10.1038/ajg.2013.79',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23609613/'
      },
      {
        title: 'Systematic review: gluten challenge in coeliac disease',
        authors: 'Leffler D, Schuppan D, Pallav K, et al.',
        publication: 'Alimentary Pharmacology & Therapeutics',
        year: 2013,
        doi: '10.1111/apt.12057',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23190238/'
      }
    ]
  },

  'pku': {
    id: 'pku',
    name: 'PKU (Phenylketonuria)',
    tagline: 'Contains phenylalanine — dangerous for PKU patients',
    tier: 'free',
    icon: '⚕️',
    color: '#E74C3C',
    description: 'Phenylketonuria (PKU) is a rare inherited metabolic disorder where the body cannot break down the amino acid phenylalanine. If left unmanaged, phenylalanine builds up to dangerous levels in the blood, causing intellectual disability and other serious health problems. People with PKU must follow a strict low-phenylalanine diet for life.',
    keyFacts: [
      'PKU affects approximately 1 in 10,000-15,000 newborns in the US',
      'Aspartame (NutraSweet, Equal) is the most common hidden source — it breaks down into phenylalanine',
      'All newborns in the US are screened for PKU within 48 hours of birth',
      'Untreated PKU can cause irreversible intellectual disability, seizures, and behavioral problems'
    ],
    ingredients: ['Aspartame', 'NutraSweet', 'Equal', 'Phenylalanine', 'L-Phenylalanine'],
    references: [
      {
        title: 'Phenylketonuria: a review of current knowledge',
        authors: 'van Spronsen FJ, Blau N, Harding C, et al.',
        publication: 'Orphanet Journal of Rare Diseases',
        year: 2021,
        doi: '10.1186/s13023-021-01840-6',
        url: 'https://pubmed.ncbi.nlm.nih.gov/33907785/'
      },
      {
        title: 'American College of Medical Genetics and Genomics therapeutic committee report: phenylalanine hydroxylase deficiency',
        authors: 'Vockley J, Andersson HC, Antshel KM, et al.',
        publication: 'Genetics in Medicine',
        year: 2014,
        doi: '10.1038/gim.2013.157',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24385075/'
      }
    ]
  },

  'g6pd': {
    id: 'g6pd',
    name: 'G6PD Deficiency',
    tagline: 'Can trigger hemolytic anemia in G6PD deficiency',
    tier: 'free',
    icon: '🩸',
    color: '#C0392B',
    description: 'Glucose-6-phosphate dehydrogenase (G6PD) deficiency is the most common enzyme deficiency worldwide, affecting over 400 million people. Certain foods and food additives can trigger hemolytic anemia — the rapid destruction of red blood cells — in affected individuals. Fava beans are the most well-known trigger, but sulfites and quinine are also concerning.',
    keyFacts: [
      'G6PD deficiency affects over 400 million people worldwide, predominantly males',
      'Fava beans (broad beans) are the most well-known dietary trigger and can cause severe hemolytic crisis',
      'Sulfites used as preservatives in wine, dried fruits, and processed foods can trigger reactions',
      'Quinine found in tonic water can also trigger hemolysis in G6PD-deficient individuals'
    ],
    ingredients: ['Fava Beans', 'Broad Beans', 'Sulfites', 'Sulfur Dioxide', 'Sodium Bisulfite', 'Quinine', 'Tonic Water'],
    references: [
      {
        title: 'Glucose-6-phosphate dehydrogenase deficiency',
        authors: 'Cappellini MD, Fiorelli G',
        publication: 'The Lancet',
        year: 2008,
        doi: '10.1016/S0140-6736(08)60073-2',
        url: 'https://pubmed.ncbi.nlm.nih.gov/18177778/'
      },
      {
        title: 'G6PD deficiency: a classic example of pharmacogenetics with on-going clinical implications',
        authors: 'Luzzatto L, Arese P',
        publication: 'British Journal of Haematology',
        year: 2018,
        doi: '10.1111/bjh.15035',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29363757/'
      }
    ]
  },

  'soy': {
    id: 'soy',
    name: 'Soy',
    tagline: 'Top 8 allergen — found in many processed foods',
    tier: 'free',
    icon: '🫘',
    color: '#7CB342',
    description: 'Soy allergy is one of the most common food allergies, particularly in infants and children. Soy is pervasive in the modern food supply, appearing not just as tofu and soy milk but as lecithin, protein isolates, and other derivatives in thousands of processed foods. While many children outgrow soy allergy, some carry it into adulthood.',
    keyFacts: [
      'Soy is one of the FDA-recognized top 8 major food allergens',
      'Approximately 0.4% of children have soy allergy, with most outgrowing it by age 10',
      'Soy lecithin is used as an emulsifier in an enormous range of processed foods including chocolate',
      'Highly refined soy oil is generally considered safe for soy-allergic individuals, but this varies'
    ],
    ingredients: ['Soy', 'Soybean', 'Tofu', 'Tempeh', 'Miso', 'Edamame', 'Soy Lecithin', 'Soy Protein', 'Soy Sauce', 'Tamari', 'TVP'],
    references: [
      {
        title: 'Soy allergy: a comprehensive review',
        authors: 'Katz Y, Gutierrez-Castrellon P, Gonzalez MG, et al.',
        publication: 'Clinical Reviews in Allergy & Immunology',
        year: 2014,
        doi: '10.1007/s12016-013-8404-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24338164/'
      },
      {
        title: 'The natural history of soy allergy',
        authors: 'Savage JH, Kaeding AJ, Matsui EC, et al.',
        publication: 'Journal of Allergy and Clinical Immunology',
        year: 2010,
        doi: '10.1016/j.jaci.2009.12.994',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20159242/'
      }
    ]
  },

  'egg': {
    id: 'egg',
    name: 'Egg',
    tagline: 'Top 8 allergen — hidden in many processed foods',
    tier: 'free',
    icon: '🥚',
    color: '#FFB74D',
    description: 'Egg allergy is the second most common food allergy in children after milk. Eggs and egg-derived ingredients appear in a wide variety of foods, from baked goods to pasta to vaccines. Both egg white and egg yolk proteins can trigger allergic reactions, though egg white allergies are more common.',
    keyFacts: [
      'Egg allergy affects approximately 2% of children, making it one of the most common childhood food allergies',
      'Most children (about 70%) outgrow egg allergy by age 16',
      'Egg proteins hide under names like albumin, lysozyme, globulin, and livetin',
      'Some egg-allergic individuals can tolerate baked egg (in muffins, cakes) but not lightly cooked egg'
    ],
    ingredients: ['Egg', 'Egg White', 'Egg Yolk', 'Albumin', 'Lysozyme', 'Mayonnaise', 'Meringue', 'Ovalbumin', 'Globulin'],
    references: [
      {
        title: 'Egg allergy in children',
        authors: 'Caubet JC, Wang J',
        publication: 'Pediatric Clinics of North America',
        year: 2011,
        doi: '10.1016/j.pcl.2011.02.003',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21453802/'
      },
      {
        title: 'The natural history of egg allergy',
        authors: 'Savage JH, Matsui EC, Skripak JM, et al.',
        publication: 'Journal of Allergy and Clinical Immunology',
        year: 2007,
        doi: '10.1016/j.jaci.2007.09.040',
        url: 'https://pubmed.ncbi.nlm.nih.gov/18082889/'
      }
    ]
  },

  'histamine': {
    id: 'histamine',
    name: 'Histamine Triggers',
    tagline: 'Can cause reactions in histamine-intolerant individuals',
    tier: 'free',
    icon: '🧬',
    color: '#AB47BC',
    description: 'Histamine intolerance occurs when the body cannot adequately break down histamine from foods, leading to a buildup that causes allergy-like symptoms. Unlike a true allergy, histamine intolerance is caused by a deficiency in the DAO enzyme. High-histamine foods, fermented products, and certain additives can trigger reactions including headaches, hives, digestive issues, and nasal congestion.',
    keyFacts: [
      'Histamine intolerance is estimated to affect 1-3% of the population, predominantly women',
      'Fermented and aged foods (vinegar, soy sauce, aged cheese, wine) are among the highest histamine sources',
      'Certain food additives like sulfites, benzoates, and tartrazine can trigger histamine release',
      'The DAO enzyme responsible for breaking down histamine can be impaired by alcohol, certain medications, and gut inflammation'
    ],
    ingredients: ['Vinegar', 'Soy Sauce', 'Miso', 'Tempeh', 'Yeast Extract', 'Citric Acid', 'MSG', 'Sodium Benzoate', 'Sulfites', 'Tartrazine'],
    references: [
      {
        title: 'Histamine and histamine intolerance',
        authors: 'Maintz L, Novak N',
        publication: 'American Journal of Clinical Nutrition',
        year: 2007,
        doi: '10.1093/ajcn/85.5.1185',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17490952/'
      },
      {
        title: 'Histamine intolerance: the current state of the art',
        authors: 'Comas-Basté O, Sánchez-Pérez S, Veciana-Nogués MT, et al.',
        publication: 'Biomolecules',
        year: 2020,
        doi: '10.3390/biom10081181',
        url: 'https://pubmed.ncbi.nlm.nih.gov/32824107/'
      },
      {
        title: 'Diamine oxidase supplementation improves symptoms in patients with histamine intolerance',
        authors: 'Izquierdo-Casas J, Comas-Basté O, Latorre-Moratalla ML, et al.',
        publication: 'Food Science and Technology International',
        year: 2019,
        doi: '10.1177/1082013218811752',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30651003/'
      }
    ]
  }
};

// Helper function to get all lenses as an array
export const getAllLenses = () => {
  return Object.values(lensesData);
};

// Helper function to get lenses by tier
export const getLensesByTier = (tier) => {
  return Object.values(lensesData).filter(lens => lens.tier === tier);
};

// Helper function to get free lenses
export const getFreeLenses = () => {
  return getLensesByTier('free');
};

// Helper function to get premium lenses
export const getPremiumLenses = () => {
  return getLensesByTier('premium');
};

// Get lens tier info for display
export const getLensTierInfo = () => {
  return {
    free: {
      name: 'Free',
      description: 'Always available with FoodLens — allergens, medical, and health lenses',
      color: '#10B981',
      lenses: getFreeLenses().map(l => l.id)
    },
    premium: {
      name: 'Premium',
      description: 'Available with FoodLens Premium ($0.99/mo) — 14-day free trial',
      color: '#8B5CF6',
      lenses: getPremiumLenses().map(l => l.id)
    }
  };
};
